import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_PROTECTION_GENERAL } from '../../../../../../../webcomponents.constants';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import {
  ExchangeProtectionGeneralFormValue,
  ExchangeGeneralKeywords,
} from '../../../../utils/uiSettings/exchange/exchange-protection.types';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { DomainIpFormValue, DomainIpItem, ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import {
  getExchangeProtectionGeneralDefaultValue,
  getExchangeProtectionGeneralSearchableKeywords,
} from '../../../../utils/uiSettings/exchange/exchange-protection.values';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { html, unsafeCSS } from 'lit';
import _ from 'lodash';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzInputNumberWebComponent } from '../../../../../../../ui/gzInput';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../ui/gzChipsGeneratorInput';
import { GzExchangeProtectionAntiSpoofingGridWebComponent } from '../gridComponents/antiSpoofing/GzExchangeProtectionAntiSpoofingGridWebComponent';
import { GzExchangeProtectionUserGroupsGridWebComponent } from '../gridComponents/userGroups/gzExchangeProtectionUserGroupsGrid';
import { GzInputWebComponent } from '../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { validateEmailPattern } from '../../helpers/utils/validators';

/**
 * @summary Type here the description of this component
 * @tag general
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_PROTECTION_GENERAL)
export class GzExchangeProtectionGeneralWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  ExchangeProtectionGeneralFormValue,
  ExchangeGeneralKeywords
> {
  protected path = {
    moduleId: ModuleId.EXCHANGE_PROTECTION,
    sectionId: SectionId.GENERAL,
  };

  protected searchableKeywords = getExchangeProtectionGeneralSearchableKeywords();

  @query('#blackList-toggle')
  private blackListToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#connection-blacklist-form-field')
  private blackListEmailInput: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;

  @query('#quarantined-days')
  private quarantineDaysInputNumber: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;

  @query('#domain-ip-toggle')
  private domainIpToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#user-groups-grid')
  private userGroupsGrid: GzExchangeProtectionUserGroupsGridWebComponent;

  @query('#anti-spoofing-grid')
  private antiSpoofingGrid: GzFormFieldWebComponent<
    GzExchangeProtectionAntiSpoofingGridWebComponent,
    DomainIpFormValue[]
  >;

  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }
  protected getValueFromManager(): ExchangeProtectionGeneralFormValue {
    const defaultValue = {
      uiSettings: {
        [ModuleId.EXCHANGE_PROTECTION]: getExchangeProtectionGeneralDefaultValue(),
      },
    };

    const value = {
      uiSettings: {
        [ModuleId.EXCHANGE_PROTECTION]: {
          blackList: PolicyPanelManager.getInstance().getPolicy('uiSettings.exchange.blackList'),
          quarantine: PolicyPanelManager.getInstance().getPolicy('uiSettings.exchange.quarantine'),
          antispoofing: PolicyPanelManager.getInstance().getPolicy('uiSettings.exchange.antispoofing'),
        },
      },
    };

    return deepmerge(defaultValue, value);
  }

  protected createFormGroup(): void {
    this.formGroup = FormGroup.build({
      uiSettings: {
        exchange: {
          blackList: {
            enable: this.blackListToggle,
            list: this.blackListEmailInput,
          },
          quarantine: {
            deleteFilesOlderThan: this.quarantineDaysInputNumber,
          },
          antispoofing: {
            enable: this.domainIpToggle,
            domainIpList: this.antiSpoofingGrid,
          },
        },
      },
    });
  }

  protected setFormData(): void {
    if (!this.valueT) {
      return;
    }
    this.formGroup.value = {
      uiSettings: {
        exchange: {
          blackList: this.valueT.uiSettings.exchange.blackList,
          quarantine: this.valueT.uiSettings.exchange.quarantine,
          antispoofing: this.valueT.uiSettings.exchange.antispoofing,
        },
      },
    };
  }

  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return {
        blackList: _.get(value, ['uiSettings', 'exchange', 'blackList'], {}),
        quarantine: _.get(value, ['uiSettings', 'exchange', 'quarantine'], {}),
        antispoofing: _.get(value, ['uiSettings', 'exchange', 'antispoofing'], {}),
      };
    }
    return value;
  }

  protected renderTitle() {
    return html` <span>
      <gz-title
        searchable-label="${this.searchableKeywords[ExchangeGeneralKeywords.General].label}"
        fontWeight="${FontWeights.MEDIUM}"
        fontSize="${FontSizes.TITLE}"
      >
        ${this.searchableKeywords[ExchangeGeneralKeywords.General].translatedLabel}
      </gz-title></span
    >`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text
      id="exchange-protection-general-info-text"
      fontSize="${FontSizes.PARAGRAPH_12}"
      fontWeight="${FontWeights.NORMAL}"
      class="infoText"
    >
      ${translateFn(
        'Create and manage groups of email accounts, define the age of the quarantined items, and ban specific senders.'
      )}
    </gz-text>`;
  }

  protected renderContent() {
    return html`<div id="exchange-protection-general-content" class="mt-4">
      ${this.renderUserGroups()} ${this.renderQuarantineSettings()} ${this.renderConnectionBlackList()}
      ${this.renderAntiSpoofing()}
    </div>`;
  }

  protected renderAntiSpoofing() {
    return html` <gz-form-field id="domain-ip-toggle" class="mbw-1 mt-4" status-field>
        <gz-toggle
          searchable-label="${this.searchableKeywords[ExchangeGeneralKeywords.DomainIpCheck].label}"
          gz-form-input
          @toggleChange="${this.onDomainIpToggleChange}"
        >
          <span>
            <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize="${FontSizes.HEADING_4}">
              ${this.searchableKeywords[ExchangeGeneralKeywords.DomainIpCheck].translatedLabel}
            </gz-title>
          </span>
        </gz-toggle>
      </gz-form-field>
      <gz-text
        id="exchange-protection-general-domain-ip-info-text"
        fontSize="${FontSizes.PARAGRAPH_12}"
        fontWeight="${FontWeights.NORMAL}"
        class="infoText"
        .innerHTML="${translateFn(
          'Use this filter to block spoofed emails by specifying the IP addresses authorized to send messages on behalf of your email domains. You can also include other trusted domains.{break}If an email claims to come from a listed domain but is sent from an unauthorized IP address, it will be rejected.{break}The filter applies only to unauthenticated email connections.',
          { break: '<br>' }
        )}"
      >
      </gz-text>
      <gz-form-field id="anti-spoofing-grid" class="hidden" status-field>
        <gz-policy-panel-exchange-protection-anti-spoofing-grid
          class="removeGridPadding"
          gz-form-input
        ></gz-policy-panel-exchange-protection-anti-spoofing-grid>
      </gz-form-field>`;
  }

  protected renderConnectionBlackList() {
    return html` <div id="connection-blacklist">
      <gz-form-field id="blackList-toggle" class="mbw-1 mt-2" status-field>
        <gz-toggle
          gz-form-input
          @toggleChange="${this.onToggleChange}"
          searchable-label="${this.searchableKeywords[ExchangeGeneralKeywords.ConnectionBlacklist].label}"
        >
          <span>${this.searchableKeywords[ExchangeGeneralKeywords.ConnectionBlacklist].translatedLabel}</span>
        </gz-toggle>
      </gz-form-field>
      <gz-form-field id="connection-blacklist-form-field" class="mt-2" status-field>
        <gz-chips-generator-input
          .validators=${[validateEmailPattern]}
          gz-form-input
          id="email-chips-generator-input"
          hasFloatingLabel
          .strings="${{
            placeholder: translateFn('Enter email addresses or domain patterns'),
            hintText: translateFn('Use a comma to separate multiple entries.'),
            clearListButtonText: translateFn('Clear list'),
            searchPlaceholder: translateFn('Search'),
            cancelClearListBtnString: translateFn('Cancel'),
            clearListActionBtnString: translateFn('Remove'),
            deleteWindowTitle: translateFn('Remove email addresses and domains?'),
            label: translateFn('Email addresses or domains'),
            deleteWindowContentText: translateFn(
              'You are about to remove all email addresses and domains from the list. This action cannot be undone.'
            ),
            notUniqueErrorMessage: translateFn('The entry already exists.'),
          }}"
          @change="${(e: CustomEvent) => {
            if (e.detail.value === undefined || e.detail.value.length === 0) {
              this.shadowRoot?.querySelector('#connection-blacklist-form-field')?.classList.remove('small-padding');
            } else {
              this.shadowRoot?.querySelector('#connection-blacklist-form-field')?.classList.add('small-padding');
            }
          }}"
        >
        </gz-chips-generator-input>
      </gz-form-field>
    </div>`;
  }
  protected renderQuarantineSettings() {
    return html`<div>
      <gz-title fontSize="${FontSizes.PARAGRAPH_11}" fontWeight="${FontWeights.SEMIBOLD}" class="form-subtitle"
        >${translateFn('Settings')}</gz-title
      >
      <gz-form-field
        id="quarantined-days"
        class="quarantine-form"
        searchable-label="${this.searchableKeywords[ExchangeGeneralKeywords.DeleteQuarantinedFiles].label}"
        label="${this.searchableKeywords[ExchangeGeneralKeywords.DeleteQuarantinedFiles].translatedLabel}"
      >
        <gz-tooltip class="tooltip-input-number" isEllipsis tooltiptext="Enter a number between 1 and 30.">
          <gz-input-number id="quarantined-days-input" min="1" max="30" gz-form-input></gz-input-number>
        </gz-tooltip>
      </gz-form-field>
    </div> `;
  }

  protected renderUserGroups() {
    return html`<div>
      <gz-title
        searchable-label="${this.searchableKeywords[ExchangeGeneralKeywords.UserGroups].label}"
        fontWeight="${FontWeights.MEDIUM}"
        fontSize="${FontSizes.HEADING_4}"
      >
        ${this.searchableKeywords[ExchangeGeneralKeywords.UserGroups].translatedLabel}
      </gz-title>
      <gz-text
        id="exchange-protection-general-user-groups-info-text"
        fontSize="${FontSizes.PARAGRAPH_12}"
        fontWeight="${FontWeights.NORMAL}"
        class="infoText mt-1"
      >
        ${translateFn(
          'Create user groups to apply customized scanning and filtering policies to specific email accounts.'
        )}
      </gz-text>
      <gz-policy-panel-exchange-protection-user-groups-grid
        id="user-groups-grid"
      ></gz-policy-panel-exchange-protection-user-groups-grid>
    </div>`;
  }

  onToggleChange(event: CustomEvent) {
    toggleVisibility(this.blackListEmailInput, event.detail.checked);
  }
  onDomainIpToggleChange(event: CustomEvent) {
    toggleVisibility(this.antiSpoofingGrid, event.detail.checked);
  }

  public reset() {
    super.reset();
    this.antiSpoofingGrid.Input.reset();
    this.userGroupsGrid.reset();
    const chipsGeneratorFormField = this.blackListEmailInput.Input.shadowRoot?.querySelector(
      '#chips-generator-form-field'
    ) as GzFormFieldWebComponent<GzInputWebComponent, string>;
    chipsGeneratorFormField?.Input?.setFormValue('');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_EXCHANGE_PROTECTION_GENERAL: GzExchangeProtectionGeneralWebComponent;
  }
}
